import {
  PathProgress,
  PathsProgressResponse,
  CourseProgress,
  CoursesProgressResponse,
  PathProgressMap,
  PlaylistProgress,
} from '@fenderdigital/api-definitions/play';

type IDMap<T> = {
  [id: string]: T;
};

export interface FlatLessonProgress {
  type: string;
  videoWatchedSeconds: number;
  videoWatchedPercentage: number;
  completed: boolean;
  updatedAt: string;
  id: string;
  composite: string[];
}

export interface FlatCourseProgress {
  type: string;
  composite: string[];
  currentLesson: string | null;
  id: string;
  completed: boolean;
  lessonsCompleted: number;
  lessonsCompletedPercent: number;
  lessonsTotal: number;
}

export interface FlatPlaylistProgress {
  composite: string[];
  currentCourse: string | null;
}

export interface FlatPathProgress {
  style: string;
  instrument: string;
  playlists: { [playlistID: string]: PlaylistProgress };
  currentPlaylist: string | null;
}

export type FlatLessonsProgressMap = IDMap<FlatLessonProgress>;
export type FlatCoursesProgressMap = IDMap<FlatCourseProgress>;
export type FlatPlaylistProgressMap = IDMap<FlatPlaylistProgress>;
export type FlatPathProgressMap = IDMap<FlatPathProgress>;

export interface FlattenedLessonsProgress {
  lessons: FlatLessonsProgressMap;
}

export interface FlattenedCoursesProgress extends FlattenedLessonsProgress {
  courses: FlatCoursesProgressMap;
}

export interface FlattenedPlaylistsProgress extends FlattenedCoursesProgress {
  playlists: FlatPlaylistProgressMap;
}

export interface FlattenedPathsProgress extends FlattenedPlaylistsProgress {
  paths: FlatPathProgressMap;
  currentPath: string | null;
}

function flattenLesson(
  lessonsProgress: CourseProgress,
  composite: string[] = [],
): FlattenedLessonsProgress {
  const { lessons } = lessonsProgress;
  const flatten: FlattenedLessonsProgress = { lessons: {} };
  if (lessons) {
    Object.keys(lessons).forEach(lessonID => {
      flatten.lessons[lessonID] = {
        type: lessons[lessonID]['lesson-type'],
        videoWatchedSeconds: lessons[lessonID]['video-watched-seconds'],
        videoWatchedPercentage: lessons[lessonID]['video-watched-percent'],
        completed: lessons[lessonID].completed,
        updatedAt: lessons[lessonID]['updated-at'],
        id: lessonID,
        composite,
      };
    });
  }
  return flatten;
}

function flattenCourses(
  coursesProgress: CoursesProgressResponse,
  composite: string[] = [],
): FlattenedCoursesProgress {
  const { courses } = coursesProgress;
  const flatten: FlattenedCoursesProgress = { courses: {}, lessons: {} };
  if (courses) {
    Object.keys(courses).forEach(courseID => {
      const course = courses[courseID];
      const {
        type,
        'current-lesson': currentLesson = null,
        completed,
        'lessons-completed': lessonsCompleted,
        'lessons-completed-percent': lessonsCompletedPercent,
        'lessons-total': lessonsTotal,
      } = course;
      flatten.courses[courseID] = {
        type,
        composite,
        currentLesson,
        id: courseID,
        completed,
        lessonsCompleted,
        lessonsCompletedPercent,
        lessonsTotal,
      };
      const flatLesson = flattenLesson(course, [...composite, courseID]);
      flatten.lessons = {
        ...flatten.lessons,
        ...flatLesson.lessons,
      };
    });
  }
  return flatten;
}

function flattenPlaylists(
  playlistsProgress: PathProgress,
  composite: string[] = [],
): FlattenedPlaylistsProgress {
  const { playlists } = playlistsProgress;
  const flatten: FlattenedPlaylistsProgress = { playlists: {}, courses: {}, lessons: {} };
  if (playlists) {
    Object.keys(playlists).forEach(playlistID => {
      flatten.playlists[playlistID] = {
        composite,
        currentCourse: playlists[playlistID]['current-course'] || null,
      };
      const flatCourse = flattenCourses(playlists[playlistID], [...composite, playlistID]);
      flatten.courses = {
        ...flatten.courses,
        ...flatCourse.courses,
      };
      flatten.lessons = {
        ...flatten.lessons,
        ...flatCourse.lessons,
      };
    });
  }
  return flatten;
}

function addPathsToFlattened(
  paths: PathProgressMap,
  flattened: FlattenedPathsProgress,
): FlattenedPathsProgress {
  const flatten = { ...flattened };
  Object.keys(paths).forEach(pathID => {
    const pathObj = paths[pathID];
    const { instrument, playlists, style } = pathObj;
    flatten.paths[pathID] = {
      style,
      instrument,
      playlists,
      currentPlaylist: pathObj['current-playlist'] || null,
    };
    const flatPlaylist = flattenPlaylists(pathObj, [pathID]);
    flatten.playlists = { ...flatten.playlists, ...flatPlaylist.playlists };
    flatten.courses = { ...flatten.courses, ...flatPlaylist.courses };
    flatten.lessons = { ...flatten.lessons, ...flatPlaylist.lessons };
  });
  return flatten;
}

// eslint-disable-next-line
// @ts-ignore // TODO add redux state type here
function flattenPaths(
  pathsProgress: PathsProgressResponse,
  state: any = {},
): FlattenedPathsProgress {
  const progressPaths = pathsProgress.paths || null;
  const { lessons, courses, playlists, paths } = state;
  const startPaths: FlatPathProgressMap = paths ? JSON.parse(JSON.stringify(paths)) : {};
  let flatten: FlattenedPathsProgress = {
    paths: startPaths,
    playlists: playlists || {},
    courses: courses || {},
    lessons: lessons || {},
    currentPath: pathsProgress['current-path'] || null,
  };

  if (progressPaths) {
    flatten = addPathsToFlattened(progressPaths, flatten);
  }
  return flatten;
}

export { flattenLesson, flattenCourses, flattenPlaylists, flattenPaths };
